/**
 * Chat Service
 *
 * Handles chat message sending with streaming support,
 * SSE parsing, and response handling.
 */

import { apiService, resolveTenantId } from './api.service.js';
import { authService } from './auth.service.js';
import { getEndpoint } from '../config/endpoints.js';
import { chatStore } from '../stores/chat.store.js';
import { userStore } from '../stores/user.store.js';
import { sessionStore } from '../utils/storage.js';
import { logger } from '../utils/logger.js';

const EMPTY_STREAM_ERROR_MESSAGE = "I'm having trouble connecting. Can you ask your question again?";

/**
 * Pause before each streamed SSE text event, purely for the typing effect.
 * AzureAPI sends one word per event, so this is a per-word delay in practice;
 * a server-appended segment (for example a fallback answer) waits once for the
 * whole segment. Interim value: the planned real-streaming change will revisit it.
 */
// PERF: typing delay cut from 50 ms to 10 ms; at 50 ms it alone added ~6-8 s to a typical answer.
export const STREAM_WORD_DELAY_MS = 10;

function isAbortError(error) {
    return error?.name === 'AbortError';
}

/**
 * Next action types returned by the bot
 */
export const NEXT_ACTIONS = {
    ENTER_ADDRESS: 'enter_address',
    LOGIN: 'login',
    GENESYS: 'genesys',
    HANDOVER_GENESYS: 'handover_genesys'
};

/**
 * ChatService class - handles chat messaging
 */
class ChatService {
    constructor() {
        this._isProcessing = false;
    }

    /**
     * Resolve the widget's current coarse DAISY stage.
     * The embeddable widget does not have DAISY's config-driven stage store, so
     * it can reliably distinguish only pre-address and post-address chat.
     *
     * @param {Object} options - Additional send options
     * @returns {'property'|'planning'|'documents'|'submission'}
     */
    _resolveCurrentStage(options = {}) {
        const validStages = new Set(['property', 'planning', 'documents', 'submission']);
        if (typeof options.stage === 'string' && validStages.has(options.stage)) {
            return options.stage;
        }

        const state = userStore.getState();
        return state?.address && state?.propId ? 'planning' : 'property';
    }

    /**
     * Send a message and get streaming response
     * @param {string} userInput - User's message
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} - Complete event data with user_config and reply
     */
    async sendMessage(userInput, options = {}) {
        if (this._isProcessing) {
            console.warn('Already processing a message');
            return null;
        }

        const shouldContinue = () => !options.signal?.aborted && (options.shouldContinue?.() ?? true);
        if (!shouldContinue()) {
            return null;
        }

        this._isProcessing = true;
        chatStore.setLoading(true);

        // Create streaming message immediately (shows typing dots while waiting for response)
        const streamingMessageId = chatStore.createStreamingMessage();

        try {
            // Server-authoritative tenant header for all requests
            const tenantId = resolveTenantId();
            if (!tenantId) {
                throw new Error('Tenant ID is required for chat streaming');
            }

            // Build request payload with v3 API structure
            const payload = this._buildRequestPayload(userInput, options, tenantId);

            // Get API URL from centralized endpoint config
            const apiUrl = getEndpoint('chatStream');
            const anonymousSessionId =
                typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('eaiAnonAssetSession') : null;

            // Make streaming request (proxy injects Authorization server-side).
            // On 401 (expired/invalid bound session), re-bootstrap and retry once.
            const buildFetchOptions = () => ({
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(tenantId && { tenant: tenantId }),
                    ...authService.getSessionHeaders(),
                    ...(anonymousSessionId && { 'x-eai-anon-asset-session': anonymousSessionId })
                },
                signal: options.signal,
                body: JSON.stringify(payload)
            });

            let response = await fetch(apiUrl, buildFetchOptions());
            if (!shouldContinue()) {
                return null;
            }

            if (response.status === 401) {
                console.warn('[ChatService] Session expired — re-initializing before retry');
                await authService.reinitialize();
                if (!shouldContinue()) {
                    return null;
                }
                response = await fetch(apiUrl, buildFetchOptions());
                if (!shouldContinue()) {
                    return null;
                }
            }

            if (!response.ok) {
                throw new Error(`API call failed with status ${response.status}`);
            }

            // Process streaming response (use existing message ID)
            const result = await this._processStreamingResponse(response, streamingMessageId, {
                signal: options.signal,
                shouldContinue
            });
            if (!shouldContinue()) {
                return null;
            }

            // Handle next action if present
            if (result?.userConfig?.workflow?.next_action) {
                this._handleNextAction(result.userConfig.workflow.next_action, result);
            }

            // Show suggestions if present
            if (result?.suggestions?.length) {
                this.dispatchSuggestions(result.suggestions);
            }

            if (options.businessRequestId) {
                const chatSessionId = result?.session?.chat_session_id || chatStore.chatSessionId;
                const ownerType =
                    result?.session?.ownerType || result?.session?.owner_type || chatStore.ownerType || 'anonymous';

                const businessRequestContext = {
                    businessRequestId: options.businessRequestId,
                    chatSessionId,
                    ownerType,
                    conversationId: result?.conversationId || chatStore.conversationId
                };

                // Preserve-on-absence: adopt the complete event's resourceapi ids only
                // when it actually carried them. Omitting the keys lets the store keep the
                // authoritative B0 create-response ids (never null an existing id because a
                // complete event dropped it — frontend DD §6.3(v)).
                if (result?.resourceApi?.planningApplicationId) {
                    businessRequestContext.resourceApiPlanningApplicationId = result.resourceApi.planningApplicationId;
                }
                if (result?.resourceApi?.assistConversationId) {
                    businessRequestContext.resourceApiAssistConversationId = result.resourceApi.assistConversationId;
                }

                chatStore.setBusinessRequestContext(businessRequestContext);

                chatStore.applyPersistedMessages({
                    userMessageId: options.userMessageId,
                    assistantMessageId: streamingMessageId,
                    followUpMessageId: result?.followUpMessageId || null,
                    persistedMessages: result?.persistedMessages,
                    businessRequestId: options.businessRequestId,
                    chatSessionId,
                    ownerType
                });
            }

            // Store project and policy data
            this._storeSessionData(result?.userConfig);

            return result;
        } catch (error) {
            if (isAbortError(error) || !shouldContinue()) {
                return null;
            }
            console.error('Chat service error:', error);
            // Update the existing streaming message with error message
            const errorMessage = "I'm having trouble connecting. Can you ask your question again?";
            chatStore.updateStreamingMessage(streamingMessageId, errorMessage);
            chatStore.finalizeStreamingMessage(streamingMessageId);
            console.error('Chat sendMessage error:', error, {
                context: 'sendMessage',
                userInput: userInput.substring(0, 100) // Truncate for logging
            });
            return null;
        } finally {
            this._isProcessing = false;
            if (shouldContinue()) {
                chatStore.setLoading(false);
            }
        }
    }

    /**
     * Build the request payload for the chat API
     * Following the new v3 API pattern
     * @param {string} userInput - User's message
     * @param {Object} options - Additional options including project, messages, businessRequestId
     * @param {string} tenantId - Resolved business tenant ID
     * @returns {Object} - Request payload
     */
    _buildRequestPayload(userInput, options = {}, tenantId) {
        // Build user_config following new pattern
        const userConfig = this._buildUserConfig(options, tenantId);

        const payload = {
            chat_input: userInput,
            stage: this._resolveCurrentStage(options),
            user_config: userConfig
        };

        // Add business_request_id if available (for project tracking)
        if (options.businessRequestId) {
            payload.business_request_id = options.businessRequestId;
        }

        // v4 ResourceAPI storage selectors (top level, snake_case) — sent iff the
        // store holds a planning application id (backend §10.1).
        Object.assign(payload, this._buildStorageSelectors());

        // Add message_history for conversational context (matches DAISY API contract)
        // Only sent when no business_request_id — with BR, backend loads history from DB
        if (!options.businessRequestId && options.messages && Array.isArray(options.messages)) {
            payload.message_history = this._convertToMessageHistory(options.messages);
        }

        // Add input modality for voice requests
        if (options.inputModality && options.inputModality !== 'text') {
            payload.input_modality = options.inputModality;
        }

        return payload;
    }

    /**
     * Build the v4 ResourceAPI storage selectors shared by the stream, chat-history,
     * and feedback bodies. Single decision rule (frontend DD §6.3(iv)): inject *iff*
     * the store holds a planning application id — its presence is the authoritative
     * "resourceapi storage" signal (storage_target is derived, not persisted). Fields
     * are snake_case for the body top level per backend §10.1.
     * @returns {Object} - Selector fields, or an empty object when ids are absent
     */
    _buildStorageSelectors() {
        const planningApplicationId = chatStore.resourceApiPlanningApplicationId;
        if (!planningApplicationId) {
            return {};
        }

        const selectors = {
            storage_target: 'resourceapi',
            planning_application_id: planningApplicationId
        };

        const assistConversationId = chatStore.resourceApiAssistConversationId;
        if (assistConversationId) {
            selectors.assist_conversation_id = assistConversationId;
        }

        return selectors;
    }

    /**
     * Convert chatStore messages to API message_history format.
     * Filters to only user/bot messages and maps to { role, content }.
     * Matches DAISY's convertToMessageHistory() contract.
     * @param {Array} messages - Messages from chatStore
     * @returns {Array<{role: string, content: string}>}
     */
    _convertToMessageHistory(messages) {
        return messages
            .filter((msg) => msg.type === 'user' || msg.type === 'bot')
            .map((msg) => ({
                role: msg.type === 'user' ? 'user' : 'assistant',
                content: msg.text || ''
            }));
    }

    /**
     * Build user_config following the new v3 API pattern
     * @param {Object} options - Additional options
     * @param {string} tenantId - Resolved business tenant ID
     * @returns {Object} - user_config object
     */
    _buildUserConfig(options = {}, tenantId) {
        const { project, isAuthenticated, userName, userEmail, userOid } = options;
        const state = userStore.getState();
        const projectData = project || sessionStore.getJSON('projectDataObject');
        const policiesData = sessionStore.getJSON('policiesDataObject');

        // Always use the original council from URL, not from projectData which may be overwritten by address validation
        const originalCouncil = this._getOriginalCouncil();

        // Determine user details based on authentication status
        const authenticated = isAuthenticated ?? state.isAuthenticated ?? false;
        const firstName = authenticated
            ? userName?.split(' ')[0] || state.userName?.split(' ')[0] || 'User'
            : 'Anonymous';
        const lastName = authenticated
            ? userName?.split(' ').slice(1).join(' ') || state.userName?.split(' ').slice(1).join(' ') || ''
            : 'User';
        const email = authenticated ? userEmail || state.email || '' : '';
        const userId = authenticated ? userOid || state.userOid || 'AnonymousUser' : 'AnonymousUser';

        // Build the project object with all property fields
        const projectObject = this._buildProjectObject(projectData, state, originalCouncil);

        return {
            tenant_id: tenantId,
            conversation_id: chatStore.conversationId,
            country: 'Australia',
            first_name: firstName,
            last_name: lastName,
            email: email,
            state: 'qa',
            user_id: userId,
            stream: true,
            workflow: {
                next_action: project?.workflow?.next_action || '',
                chat_migrated: project?.workflow?.chat_migrated || false,
                execution_logs: project?.workflow?.execution_logs || {}
            },
            project: projectObject,
            policies: policiesData || {
                state: {},
                local: {}
            }
        };
    }

    /**
     * Build the project object for user_config
     * @param {Object} projectData - Project data from session or options
     * @param {Object} state - User store state
     * @param {string} originalCouncil - Original council from URL
     * @returns {Object} - Project object
     */
    _buildProjectObject(projectData, state, originalCouncil) {
        if (projectData) {
            return {
                ...projectData,
                // Always override with latest state values
                council_name: originalCouncil || projectData.council_name,
                address: state.address || projectData.address,
                zone: state.zone || projectData.zone,
                zone_description: state.zoneDescription || projectData.zone_description,
                prop_id: state.propId
                    ? Number(state.propId)
                    : projectData.prop_id
                      ? Number(projectData.prop_id)
                      : undefined,
                lot_width: state.lotWidth || projectData.lot_width,
                lot_size: state.lotSize || projectData.lot_size,
                floor_space_ratio: state.floorSpaceRatio || projectData.floor_space_ratio,
                mine_subsidence_affects:
                    state.mineSubsidenceAffects !== null && state.mineSubsidenceAffects !== undefined
                        ? state.mineSubsidenceAffects
                        : (projectData.mine_subsidence_affects ?? null),
                active_precinct: state.activePrecinct || projectData.active_precinct || '',
                property_precinct:
                    state.propertyPrecinct || projectData.property_precinct || projectData.propertyPrecinct || null,
                is_bushfire_zone: state.bushFireZone !== undefined ? state.bushFireZone : projectData.is_bushfire_zone,
                is_flood_zone: state.floodZone !== undefined ? state.floodZone : projectData.is_flood_zone,
                is_heritage_zone: state.heritageZone !== undefined ? state.heritageZone : projectData.is_heritage_zone,
                // INVARIANT: Explicit empty controls override stale persisted project values.
                min_lot_size: state.minLotSize ?? projectData.min_lot_size ?? '',
                max_building_height: state.maxBuildingHeight ?? projectData.max_building_height ?? '',
                is_battle_axe_lot:
                    state.isBattleAxeLot !== undefined ? state.isBattleAxeLot : projectData.is_battle_axe_lot || false,
                is_corner_lot: state.isCornerLot !== undefined ? state.isCornerLot : projectData.is_corner_lot || false
            };
        }

        // Default project object when no project data exists
        return {
            council_name: originalCouncil || state.councilName || '',
            address: state.address || undefined,
            zone: state.zone || undefined,
            zone_description: state.zoneDescription || undefined,
            prop_id: state.propId ? Number(state.propId) : undefined,
            active_precinct: state.activePrecinct || '',
            property_precinct: state.propertyPrecinct || null,
            application_name: '',
            development_type: '',
            planning_pathway: '',
            lot_width: state.lotWidth || undefined,
            lot_size: state.lotSize || undefined,
            floor_space_ratio: state.floorSpaceRatio || undefined,
            mine_subsidence_affects: state.mineSubsidenceAffects ?? null,
            project_size: '',
            building_height: '',
            sub_division: '',
            front_setback: '',
            side_setback: '',
            rear_setback: '',
            is_bushfire_zone: state.bushFireZone || false,
            is_flood_zone: state.floodZone || false,
            is_heritage_zone: state.heritageZone || false,
            matched_pathways: [],
            failed_pathways: [],
            additional_criteria: '',
            child_restraint_barriers: '',
            min_lot_size: state.minLotSize || '',
            max_building_height: state.maxBuildingHeight || '',
            dwelling_density: '',
            is_battle_axe_lot: state.isBattleAxeLot || false,
            is_corner_lot: state.isCornerLot || false,
            documents: [],
            uploaded_documents: [],
            flags: {}
        };
    }

    /**
     * Process streaming SSE response
     * @param {Response} response - Fetch response
     * @param {string} [existingMessageId] - Optional existing streaming message ID to use
     * @returns {Promise<Object>} - Parsed result with userConfig and suggestions
     */
    async _processStreamingResponse(response, existingMessageId = null, options = {}) {
        const shouldContinue = () => !options.signal?.aborted && (options.shouldContinue?.() ?? true);
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let done = false;
        let completeEventData = null;
        let isJsonStreaming = false;
        let jsonBuffer = '';
        let linkBuffer = '';
        let bufferingLink = false;
        let followUpMessageId = null;
        let eventBuffer = '';
        let hasReceivedStreamContent = false;

        // Use existing message ID or create new one
        let messageId = existingMessageId || chatStore.createStreamingMessage();

        while (!done) {
            if (!shouldContinue()) {
                try {
                    await reader.cancel();
                } catch {
                    // Ignore cancellation cleanup errors.
                }
                return null;
            }
            const { value, done: doneReading } = await reader.read();
            done = doneReading;

            const chunk = decoder.decode(value, { stream: true });
            if (!chunk) continue;

            eventBuffer += chunk;
            const events = eventBuffer.split('\n\n');
            eventBuffer = events.pop() || '';

            for (const event of events) {
                if (!shouldContinue()) {
                    return null;
                }
                // Handle data events (streaming text)
                if (event.startsWith('data: ')) {
                    let textChunk = this._parseStreamDataChunk(event.replace(/^data:\s*/, '').trim());

                    // Skip [DONE] marker
                    if (textChunk === '[DONE]') {
                        done = true;
                        break;
                    }

                    // Handle JSON block start
                    if (textChunk.startsWith('```json') || textChunk === '```json') {
                        isJsonStreaming = true;
                        jsonBuffer = '';
                        continue;
                    }

                    // Handle JSON block end
                    if (textChunk === '```' && isJsonStreaming) {
                        chatStore.finalizeStreamingMessage(messageId);
                        const jsonMessageId = chatStore.createStreamingMessage();
                        const cleanJson = this._decodeUnicode(jsonBuffer.trim());
                        chatStore.updateStreamingMessage(jsonMessageId, `<pre>${cleanJson}</pre>`);
                        chatStore.finalizeStreamingMessage(jsonMessageId);
                        isJsonStreaming = false;
                        jsonBuffer = '';
                        continue;
                    }

                    // Accumulate JSON content
                    if (isJsonStreaming) {
                        jsonBuffer += ' ' + textChunk;
                        continue;
                    }

                    // Handle follow-up questions (creates new message bubble)
                    if (textChunk.toLowerCase().includes('follow-up:')) {
                        chatStore.finalizeStreamingMessage(messageId);
                        messageId = chatStore.createStreamingMessage();
                        followUpMessageId = messageId;
                        const followUpText = textChunk.replace(/follow-up:/i, '').trim();
                        if (!followUpText) continue;
                        textChunk = followUpText;
                    }

                    // Clean the text chunk
                    let cleanedText = this._cleanTextChunk(textChunk);

                    // Skip junk tokens
                    if (this._shouldSkipToken(cleanedText, textChunk)) continue;

                    // Handle anchor/link buffering
                    if (cleanedText.startsWith('[<a') || cleanedText.startsWith('<a') || bufferingLink) {
                        bufferingLink = true;
                        linkBuffer += ' ' + cleanedText;

                        if (cleanedText.includes('</a>')) {
                            bufferingLink = false;
                            const decoratedLink = this._decorateLink(linkBuffer);
                            chatStore.appendToStreamingMessage(messageId, ' ' + decoratedLink);
                            linkBuffer = '';
                        }
                        continue;
                    }

                    // PERF: short per-event typing delay; see STREAM_WORD_DELAY_MS.
                    await new Promise((resolve) => setTimeout(resolve, STREAM_WORD_DELAY_MS));
                    if (!shouldContinue()) {
                        return null;
                    }
                    chatStore.appendToStreamingMessage(messageId, cleanedText);
                    hasReceivedStreamContent = true;
                }
                // Handle complete event (final metadata)
                else if (event.startsWith('event: complete')) {
                    const payloadStart = event.indexOf('{');
                    if (payloadStart !== -1) {
                        try {
                            completeEventData = JSON.parse(event.slice(payloadStart).trim());
                        } catch (e) {
                            console.error('Failed to parse complete event JSON:', e);
                        }
                    }
                    done = true;
                    break;
                }
            }
        }

        const finalEvent = eventBuffer.trim();
        if (finalEvent.startsWith('data: ')) {
            if (!shouldContinue()) {
                return null;
            }
            const textChunk = this._parseStreamDataChunk(finalEvent.replace(/^data:\s*/, '').trim());
            const cleanedText = this._cleanTextChunk(textChunk);
            if (!this._shouldSkipToken(cleanedText, textChunk)) {
                chatStore.appendToStreamingMessage(messageId, cleanedText);
                hasReceivedStreamContent = true;
            }
        } else if (finalEvent.startsWith('event: complete')) {
            const payloadStart = finalEvent.indexOf('{');
            if (payloadStart !== -1) {
                try {
                    completeEventData = JSON.parse(finalEvent.slice(payloadStart).trim());
                } catch (e) {
                    console.error('Failed to parse complete event JSON:', e);
                }
            }
        }

        if (!shouldContinue()) {
            return null;
        }

        if (!hasReceivedStreamContent && !completeEventData) {
            chatStore.updateStreamingMessage(messageId, EMPTY_STREAM_ERROR_MESSAGE);
        }

        // Finalize the streaming message
        chatStore.finalizeStreamingMessage(messageId);

        // Parse and return result, attaching follow-up ID when present
        const result = this._parseCompleteEvent(completeEventData);
        if (!result) return null;

        if (!shouldContinue()) {
            return null;
        }

        if (result.question && !followUpMessageId) {
            // INVARIANT: The complete event carries the canonical follow-up if
            // the stream delimiter was dropped before the browser rendered it.
            const backfilledFollowUpId = chatStore.createStreamingMessage();
            chatStore.updateStreamingMessage(backfilledFollowUpId, result.question);
            chatStore.finalizeStreamingMessage(backfilledFollowUpId);
            followUpMessageId = backfilledFollowUpId;
        }

        return { ...result, followUpMessageId };
    }

    /**
     * Parse one SSE data payload. The QA API currently streams JSON string
     * fragments (for example: data: "hello"), while older paths sent raw text.
     * @param {string} textChunk - Raw data payload
     * @returns {string} - Parsed text payload
     */
    _parseStreamDataChunk(textChunk) {
        if (!textChunk || textChunk === '[DONE]') return textChunk;

        try {
            const parsed = JSON.parse(textChunk);
            return typeof parsed === 'string' ? parsed : textChunk;
        } catch {
            return textChunk;
        }
    }

    /**
     * Clean a text chunk from streaming response
     * @param {string} textChunk - Raw text chunk
     * @returns {string} - Cleaned text
     */
    _cleanTextChunk(textChunk) {
        let cleaned = textChunk
            // Remove leading/trailing quotes
            .replace(/^"+|"+$/g, '')
            // Remove escaped quotes
            .replace(/\\"/g, '')
            // Convert literal \n\n to line breaks
            .replace(/\\n\\n/g, '\n\n')
            // Convert literal \n to line break
            .replace(/\\n/g, '\n');

        // Decode unicode escape sequences
        cleaned = this._decodeUnicode(cleaned);

        // Remove stray braces only (not brackets — brackets are used in markdown links)
        cleaned = cleaned.replace(/[{}]/g, '');

        return cleaned;
    }

    /**
     * Check if a token should be skipped
     * @param {string} cleanedText - Cleaned text
     * @param {string} originalText - Original text chunk
     * @returns {boolean} - True if should skip
     */
    _shouldSkipToken(cleanedText, originalText) {
        if (originalText === '[DONE]' || cleanedText === '[DONE]') return true;
        if (cleanedText === '') return true;

        const structuralJunk = ['{', '}', '[', ']', '```', '```json', 'json'];
        if (structuralJunk.includes(cleanedText)) return true;

        if (cleanedText === 'response' || cleanedText === ':') return true;

        if (cleanedText === 'data' && !originalText.includes('"')) return true;

        return false;
    }

    /**
     * Decode unicode escape sequences
     * @param {string} str - String with possible unicode escapes
     * @returns {string} - Decoded string
     */
    _decodeUnicode(str) {
        if (!str) return str;

        try {
            return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
                return String.fromCharCode(parseInt(hex, 16));
            });
        } catch (e) {
            return str;
        }
    }

    /**
     * Decorate a link with target and styling
     * @param {string} linkBuffer - Raw link buffer
     * @returns {string} - Decorated link HTML
     */
    _decorateLink(linkBuffer) {
        const rawAnchor = linkBuffer
            .trim()
            .replace(/^\[\s*/, '')
            .replace(/\s*\]$/, '');

        return rawAnchor.replace(
            /<a\s+href=['"]([^'"]+)['"]/i,
            '<a href="$1" target="_blank" rel="noopener noreferrer" class="chat-link"'
        );
    }

    /**
     * Parse complete event data
     * @param {Object} completeEventData - Raw complete event
     * @returns {Object} - Parsed result
     */
    _parseCompleteEvent(completeEventData) {
        if (!completeEventData) return null;

        let suggestions = [];
        let userConfig = completeEventData.user_config;
        let question = null;

        // Parse reply for suggestions and detect development type question
        if (completeEventData.reply) {
            try {
                const parsedReply =
                    typeof completeEventData.reply === 'string'
                        ? JSON.parse(completeEventData.reply)
                        : completeEventData.reply;

                if (parsedReply?.suggestions?.length) {
                    suggestions = parsedReply.suggestions;
                }
                if (typeof parsedReply?.question === 'string' && parsedReply.question.trim()) {
                    question = parsedReply.question;
                }
            } catch (e) {
                console.error('Failed to parse reply JSON:', e);
            }
        }

        return {
            userConfig,
            suggestions,
            question,
            conversationId: completeEventData.conversation_id,
            context: completeEventData.context,
            persistedMessages: completeEventData.persisted_messages || null,
            session: completeEventData.session || null,
            resourceApi: this._extractResourceApiIds(completeEventData)
        };
    }

    /**
     * Extract server-returned ResourceAPI ids from a complete event. Total extraction
     * (never throws on absent/malformed fields) — a missing shape yields null so the
     * post-complete store write preserves the authoritative B0 create-response ids.
     *
     * The resourceapi field path lives here in ONE place so the A3 shape verification
     * (frontend DD §14 item 8) adjusts a single location.
     * @param {Object} completeEventData - Raw complete event
     * @returns {{planningApplicationId: string|null, assistConversationId: string|null}|null}
     */
    _extractResourceApiIds(completeEventData) {
        const source = completeEventData?.resourceapi || completeEventData?.session?.resourceapi;
        if (!source) {
            return null;
        }

        return {
            planningApplicationId: source.planning_application_id || null,
            assistConversationId: source.assist_conversation_id || null
        };
    }

    /**
     * Handle next action from bot response
     * @param {string} nextAction - Action type
     * @param {Object} result - Full result object
     */
    _handleNextAction(nextAction, result) {
        // Dispatch custom event for the component to handle
        const event = new CustomEvent('chat-next-action', {
            detail: {
                action: nextAction,
                result
            },
            bubbles: true,
            composed: true
        });

        document.dispatchEvent(event);
    }

    /**
     * Dispatch suggestions event
     * @param {Array} suggestions - Suggestion strings
     */
    dispatchSuggestions(suggestions) {
        const event = new CustomEvent('chat-suggestions', {
            detail: { suggestions },
            bubbles: true,
            composed: true
        });

        document.dispatchEvent(event);
    }

    /**
     * Store session data from response
     * @param {Object} userConfig - User config from response
     */
    _storeSessionData(userConfig) {
        if (!userConfig) return;

        if (userConfig.project) {
            sessionStore.setJSON('projectDataObject', userConfig.project);

            // Save development_type to userStore.currentProject when returned by the API
            if (userConfig.project.development_type) {
                if (!userStore.getCurrentProject()) {
                    userStore.setCurrentProject({});
                }
                userStore.updateProjectProperty('development_type', userConfig.project.development_type);
            }
        }

        if (userConfig.policies) {
            sessionStore.setJSON('policiesDataObject', userConfig.policies);
        }
    }

    /**
     * Load canonical BR-backed structured chat history.
     *
     * @param {string} businessRequestId - Business request ID
     * @param {number} [limit=50] - Message limit
     * @returns {Promise<Object|null>}
     */
    async fetchChatHistory(businessRequestId, limit = 50, options = {}) {
        if (!businessRequestId) {
            return null;
        }

        try {
            const apiUrl = getEndpoint('chatHistory');
            const body = {
                business_request_id: businessRequestId,
                limit,
                // v4 scope selectors — with ids present the ≥1 scope id requirement is
                // satisfied by construction, so history never 400s MISSING_HISTORY_SCOPE.
                ...this._buildStorageSelectors()
            };
            return options.signal
                ? await apiService.post(apiUrl, body, { signal: options.signal })
                : await apiService.post(apiUrl, body);
        } catch (error) {
            if (isAbortError(error)) {
                throw error;
            }
            console.error('Chat history fetch error:', error);
            return null;
        }
    }

    /**
     * Send structured feedback for a persisted BR-backed message.
     *
     * @param {Object} params - Feedback payload
     * @param {string} params.businessRequestId - Business request ID
     * @param {string} params.messageId - Persisted backend message ID
     * @param {'up'|'down'} params.feedback - Selected feedback
     * @returns {Promise<boolean>} - Success status
     */
    async sendFeedback({ businessRequestId, messageId, feedback }) {
        if (!businessRequestId || !messageId) {
            return false;
        }

        try {
            const apiUrl = getEndpoint('chatFeedback');

            await apiService.post(apiUrl, {
                business_request_id: businessRequestId,
                message_id: messageId,
                feedback: feedback === 'up' ? 'good' : 'bad',
                // v4 storage selectors — routed to ResourceAPI iff ids are present.
                ...this._buildStorageSelectors()
            });

            return true;
        } catch (error) {
            // Non-blocking by contract: feedback failure must never break the chat flow,
            // so the return value stays false and the UI is unaffected. During the v4
            // rollout a selector rejection must still be observable — prod visibility is
            // the BFF ≥400 telemetry (EAI_USER_EXPERIENCE_TELEMETRY_ENABLED); this warn is
            // the dev-side signal (frontend DD §6.3(iv)).
            logger.warn('[ChatService] feedback send failed', error?.message || error);
            return false;
        }
    }

    /**
     * Check if currently processing a message
     * @returns {boolean}
     */
    get isProcessing() {
        return this._isProcessing;
    }

    /**
     * Get original council from URL/config (not from userStore which may be overwritten by API)
     * This ensures we always use the council from the URL for handover decisions
     * @returns {string} - Original council name
     */
    _getOriginalCouncil() {
        // Check DAISY_CONFIG first (set by eaidev.html)
        if (typeof window !== 'undefined' && window.DAISY_CONFIG?.council) {
            return window.DAISY_CONFIG.council;
        }

        // Check URL parameters directly
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlCouncil = urlParams.get('Council') || urlParams.get('council');
            if (urlCouncil) return urlCouncil;
        }

        // Check session storage only after current embed context
        if (typeof sessionStorage !== 'undefined') {
            const daisyCouncil = sessionStorage.getItem('daisy_council');
            if (daisyCouncil) return daisyCouncil;
        }

        return '';
    }
}

// Export singleton instance
export const chatService = new ChatService();
